import * as d3 from 'd3';
import { EnterElement } from 'd3';
import _ from 'lodash';
import { store } from '../../configureStore';
import { filterOnDestinationMetric, toggleDestinationMetricAndUpdate } from './actions';
import { filterOffIcon, filterOnIcon, pathwayOffIcon, pathwayOnIcon } from './PathwayIcons';
import {
    getDatasetTheme,
    getNoDataTheme,
    lengendData,
    metricConfig,
    MetricConfigType,
    pathwayTheme
} from './PathwayTheme';
import { EventMetricWithConfig } from './types/CohortPathway';
import { DestinationMetric, DestinationMetricWithConfig, EventMetric, hasData, hasDestinationFilter, isExcludedFromPathway } from './types/EventMetric';

export function drawLegend(legend: d3.Selection<SVGGElement, unknown, HTMLElement, any>) {
    const padding = 5
    const iconSize = 15
    const fontSize = 10
    const rowHeight = padding * 2 + iconSize
    const legendWidth = pathwayTheme.index.legend.width

    // border
    legend
        .append("path")
        .attr("d", roundedRect(0, 0, legendWidth, 200, 4))
        .style("fill", (d, i) => '#fdfdfd')
        .attr("stroke-width", .5)
        .attr("stroke", "#ebeef2")

    const legendItems = legend
        .selectAll()
        .data(lengendData)
        .enter()

    const calcY = (index: number, size: number, padding: number) => {
        return padding + (index * padding * 2) + (index * size)
    }

    // icons
    legendItems
        .each(function (d, index) {
            const selection = (d3.select(this) as d3.Selection<EnterElement, DestinationMetricWithConfig, null, undefined>).append('g')
            d.icon(selection, { size: iconSize, x: padding, y: calcY(index, iconSize, padding) })
        })

    // text
    legendItems
        .append("text")
        .attr("fill", "#788496")
        .attr("font-family", "sans-serif")
        .attr("font-size", `${fontSize}px`)
        .attr("x", padding + iconSize + 5)
        .attr("y", (d, index) => (fontSize + fontSize / 4) + calcY(index, iconSize, padding) - ((d.label.length - 1) * fontSize / 2))
        .selectAll()
        .data((d, index: number) => {
            return d
                .label
                .map(i => {
                    return { pIndex: index, label: i }
                })
        })
        .enter()
        .append("tspan")
        .attr("x", padding + iconSize + 5)
        .attr("dy", (d, index) => {
            return (fontSize * index)
        })
        .text((d: any, index: number) => {
            return d.label
        })

    // divider
    legendItems
        .append("line")
        .attr("stroke", "#efefef")
        .attr("x1", padding)
        .attr("y1", (d, index) => rowHeight * index)
        .attr("x2", legendWidth - (padding * 2))
        .attr("y2", (d, index) => rowHeight * index)
        .attr("stroke-width", .5)
        .attr("visibility", (d, index) => index === 0 ? "hidden" : "")

    // delete all dividers that are hidden
    legendItems
        .selectAll("*[visibility=hidden]")
        .remove()
}

export function drawMetricBoxHeaderText(group: d3.Selection<SVGGElement, DestinationMetricWithConfig, SVGGElement, EventMetric | unknown>, colour: string = "white") {
    const padding = pathwayTheme.metric.padding.left;
    const config = pathwayTheme.metric.header;

    const title = group
        .append("text")
        .style("fill", colour)

    title
        .append("tspan")
        .attr("font-family", config.font.fontFamily)
        .attr("font-size", `${config.font.size}px`)
        .text((d, i) => getDatasetTheme(d.metric.dataset).title)

    title
        .filter((d, i) => hasData(d.metric))
        .append("tspan")
        .attr("data-testid", (d, i) => "suppressed")
        .attr("font-family", config.subFont.fontFamily)
        .attr("font-size", `${config.subFont.size}px`)
        .attr("font-weight", config.subFont.fontWeight)
        .text((d, i) => (d.metric as DestinationMetric).patientCount.toLocaleString())
        .attr("x", padding)
        .attr("dy", (d, i, n) => {
            const previousSiblingRect = (n[i].previousSibling as SVGTSpanElement).getBBox()
            return previousSiblingRect.height + 2
        })

    title
        .attr("x", padding)
        .attr("y", config.height / 2 - config.radius)
}

export function drawMetricBoxHeader(group: d3.Selection<SVGGElement, DestinationMetricWithConfig, SVGGElement, EventMetric | unknown>) {
    const config = pathwayTheme.metric.header;
    const metricBoxWidth = config.width
    const headerHeight = config.height
    const radius = config.radius

    group
        .append("path")
        .attr("d", topRoundedRect(0, 0, metricBoxWidth, headerHeight, radius))
        .style("fill", (d, i) => {
            if (hasData(d.metric)) {
                return getDatasetTheme(d.metric.dataset).colour
            } else {
                return getNoDataTheme().colour
            }
        })
        .attr("stroke-width", 1)
        .attr("stroke", "#e7ebf0")

    drawMetricBoxHeaderText(group)

    // action icons - destination filters
    group
        .filter((d, i) => hasData(d.metric) || hasDestinationFilter(d))
        .each(function (d) {
            const filterIconGroup = (d3.select(this) as d3.Selection<SVGGElement, DestinationMetricWithConfig, null, undefined>)
                .append('g')
                .attr("class", "filter-icon")
                .on("click", (d, i) => {
                    store.dispatch(filterOnDestinationMetric(d.metric as DestinationMetric))
                })

            if (hasDestinationFilter(d)) {
                filterOnIcon(filterIconGroup, pathwayTheme.metric.actions.filter.icon)
            } else {
                filterOffIcon(filterIconGroup, pathwayTheme.metric.actions.filter.icon)
            }
        })
}

export function metricBox(group: d3.Selection<SVGGElement, DestinationMetricWithConfig, SVGGElement, EventMetricWithConfig>) {
    const padding = pathwayTheme.metric.padding.left;
    const config = pathwayTheme.metric.header;
    const metricBoxWidth = config.width
    const headerHeight = config.height
    const radius = config.radius

    // header
    drawMetricBoxHeader(group)

    // content
    group
        .append("path")
        .attr("d", (d, i) => {
            const contentHeight = pathwayTheme.metric.content.height(metricConfig[d.metric.dataset].length)
            return bottomRoundedRect(0, headerHeight, metricBoxWidth, contentHeight, radius)
        })
        .style("fill", "white")
        .attr("stroke-width", 1)
        .attr("stroke", "#e7ebf0")

    const height = pathwayTheme.metric.content.height(1)

    const eachMetricLineSelection = group
        .filter((d, i) => hasData(d.metric))
        .selectAll()
        .data((d, i) => metricConfig[d.metric.dataset].map(i => {
            return {
                metric: d.metric as DestinationMetric,
                config: i
            }
        }))
        .enter()
        .append("g")
        .attr("transform", (d, index) => `translate(${padding}, ${headerHeight + index * height})`)

    drawMetrics(eachMetricLineSelection)

    // action icons - pathway include/exclude
    group
        .filter((d, i) => hasData(d.metric) || isExcludedFromPathway(d))
        .each(function (d) {
            const pathwayIconGroup = (d3.select(this) as d3.Selection<SVGGElement, DestinationMetricWithConfig, null, undefined>)
                .append('g')
                .attr("class", "pathway-include")
                .on("click", (d, i) => {
                    store.dispatch(toggleDestinationMetricAndUpdate(d.metric as DestinationMetric))
                })
            const contentHeight = pathwayTheme.metric.content.height(metricConfig[d.metric.dataset].length)
            const dimensions = {
                size: pathwayTheme.metric.actions.include.icon.size,
                x: pathwayTheme.metric.actions.include.icon.x,
                y: pathwayTheme.metric.actions.include.icon.y(contentHeight)
            }
            const index = _.indexOf(d.config.datasets, d.metric.dataset)
            if (index >= 0) {
                pathwayOnIcon(pathwayIconGroup, dimensions)
            } else {
                pathwayOffIcon(pathwayIconGroup, dimensions)
            }
        })
}

export function drawMetrics(eachMetricLineSelection: d3.Selection<SVGGElement, { metric: DestinationMetric, config: MetricConfigType }, SVGElement, DestinationMetricWithConfig>) {
    const metricBoxWidth = pathwayTheme.metric.header.width
    const iconSize = pathwayTheme.metric.content.icon.size
    const padding = pathwayTheme.metric.padding.left
    const fontSize = pathwayTheme.metric.content.font.size
    const heightOfEachLine = pathwayTheme.metric.content.height(1)

    // icon
    eachMetricLineSelection
        .each(function (d, index) {
            const selection = (d3.select(this) as d3.Selection<SVGGElement, DestinationMetricWithConfig, null, undefined>).append('g')
            d.config.icon(selection, { size: iconSize, x: 0, y: heightOfEachLine * 0.25 })
        })

    // metric text
    eachMetricLineSelection
        .append("text")
        .attr("x", iconSize + fontSize / 2)
        .attr("y", heightOfEachLine - fontSize / 2)
        .attr("font-family", pathwayTheme.metric.content.font.fontFamily)
        .attr("font-size", `${fontSize}px`)
        .attr("data-testid", (d, i) => d.config.suppressed ? "suppressed" : null)
        .style("fill", "#636b72")
        .text((d, i) => d.config.value(d.metric))

    eachMetricLineSelection
        .append("line")
        .attr("stroke", "#efefef")
        .attr("x1", 0)
        .attr("y1", heightOfEachLine)
        .attr("x2", metricBoxWidth - padding * 2)
        .attr("y2", heightOfEachLine)
        .attr("stroke-width", 1)
        .attr("visibility", (d, index) => {
            // hide the last divider
            return index === metricConfig[d.metric.dataset].length - 1 ? "hidden" : ""
        })
}

export function roundedRect(x: number, y: number, width: number, height: number, radius: number) {
    return "M" + (x + radius) + "," + y
        + "h" + (width - 2 * radius)
        + "a" + radius + "," + radius + " 0 0 1 " + radius + "," + radius
        + "v" + (height - 2 * radius)
        + "a" + radius + "," + radius + " 0 0 1 " + -radius + "," + radius
        + "h" + -(width - 2 * radius)
        + "a" + radius + "," + radius + " 0 0 1 " + -radius + "," + -radius
        + "v" + -(height - 2 * radius)
        + "a" + radius + "," + radius + " 0 0 1 " + radius + "," + -radius
        + "z";
}

export function topRoundedRect(x: number, y: number, width: number, height: number, radius: number) {
    return "M" + (x + radius) + "," + y
        + "h" + (width - 2 * radius)
        + "a" + radius + "," + radius + " 0 0 1 " + radius + "," + radius
        + "v" + (height - radius)
        + "h" + -width
        + "v" + -(height - radius)
        + "a" + radius + "," + radius + " 0 0 1 " + radius + "," + -radius
        + "z";
}

export function bottomRoundedRect(x: number, y: number, width: number, height: number, radius: number) {
    return "M" + (x) + "," + y
        + "h" + (width)
        + "v" + (height - radius)
        + "a" + radius + "," + radius + " 0 0 1 " + -radius + "," + radius
        + "h" + -(width - 2 * radius)
        + "a" + radius + "," + radius + " 0 0 1 " + -radius + "," + -radius
        + "v" + -(height - radius)
        + "z";
}